import { useGSAP } from '@gsap/react'
import { useConfigurationStore } from '@stores/configurationStore'
import { useJackpotStore } from '@stores/jackpotStore'
import { Slot } from '@utils/Slot'
import gsap from 'gsap'
import React, { ReactNode, useRef, useState } from 'react'
import { ICommonJackpotConfig, JackpotOdometer } from '@amusnet/react-ui'
import { JackpotSuits, JackpotLinkTypes, JackpotEQLevels, JackpotType } from 'game-ui-types'
import JackpotStatisticsEQShining from '../JackpotEQ/JackpotStatisticsEQShining'

interface JackpotStatisticsItemProps {
    type: JackpotSuits | JackpotLinkTypes | JackpotEQLevels
    config: ICommonJackpotConfig
    children: ReactNode | ReactNode[]
    realTimeAmount?: number
    isPanelActive?: boolean
    amount: number
    winningUser: string
    isAmountStatic: boolean
    shouldUseOdometer: boolean
    jackpotType?: JackpotType
    onWinningUserPresentationComplete: () => void
    onBlinkingComplete: () => void
    onClick?: () => void
    onMouseOver?: () => void
    onMouseOut?: () => void
    onRealTimeOdometerUpdate?: (value: number) => void
}

/**
 * JackpotStatisticsItem Component
 *
 * Dynamically adjusts size and font based on the viewport and the type/suit.
 * Accepts "above" and "below" Slots so that the statistics is sandwiched in between jackpot specific elements.
 */
const JackpotStatisticsItem: React.FC<JackpotStatisticsItemProps> = ({
    type,
    config,
    children,
    realTimeAmount,
    isPanelActive,
    amount,
    winningUser,
    isAmountStatic,
    shouldUseOdometer,
    jackpotType,
    onWinningUserPresentationComplete,
    onBlinkingComplete,
    onClick,
    onMouseOver,
    onMouseOut,
    onRealTimeOdometerUpdate,
}) => {
    const currency = useConfigurationStore((state) => state.currency)
    const currencyHideCents = useConfigurationStore((state) => state.currencyHideCents)
    const shouldHideJackpotWinnerNames = useConfigurationStore((state) => state.shouldHideJackpotWinnerNames)
    const viewport = useConfigurationStore((state) => state.viewport)
    const viewportConfig = config[viewport]
    const amountContainerRef = useRef<HTMLDivElement>(null)
    const eqUsernameRef = useRef<HTMLSpanElement>(null)
    const duration = useJackpotStore((state) => state.jackpotStatsAnimationDurationSeconds)
    const countdownToZeroDuration = useJackpotStore((state) => state.jackpotStatsCountdownToZeroDurationSeconds)
    const currencyHideCentsOnlyInJackpotBoxes = useJackpotStore((state) => state.currencyHideCentsOnlyInJackpotBoxes)
    const hideCents = currencyHideCentsOnlyInJackpotBoxes || currencyHideCents
    const [shownUsername, setShownUsername] = useState(winningUser)
    const [currentWinTween, setCurrentWinTween] = useState<gsap.core.Tween | null>(null)

    useGSAP(() => {
        // If we do not have a winning user stop any previously running animations and reset
        if (!winningUser) {
            if (shownUsername) {
                setShownUsername('')
            }

            if (currentWinTween) {
                currentWinTween?.kill()

                if (amountContainerRef.current) amountContainerRef.current.style.opacity = '1'

                if (eqUsernameRef.current) eqUsernameRef.current.style.opacity = '1'

                setCurrentWinTween(null)
                // Mimic end of animation to let parent know to clean up
                onWinningUserPresentationComplete()
            }

            return
        }

        const username = winningUser.length > 10 ? `${winningUser.substring(0, 10)}...` : winningUser

        if (username === shownUsername) return

        // If we have received a username to show, make sure we are not already showing a username,
        // if we were kill the animation and start over with the latest data
        currentWinTween?.kill()
        setShownUsername(username)

        const repeat = 9
        let i = 0

        setCurrentWinTween(
            gsap.fromTo(
                [amountContainerRef.current, eqUsernameRef.current],
                {
                    opacity: 1,
                },
                {
                    opacity: 0,
                    yoyo: true,
                    repeat,
                    delay: 0.5,
                    duration: duration / 10,
                    ease: 'power1.inOut',
                    onRepeat: () => {
                        i = i + 1

                        if (i === repeat) {
                            // On the last iteration let parent know to show latest jackpot data
                            onBlinkingComplete()
                        }
                    },
                    onComplete: () => {
                        if (!winningUser) return

                        setCurrentWinTween(
                            gsap.delayedCall(duration * 2, () => {
                                // keep username showing a bit longer and then call parent to clean up
                                setCurrentWinTween(null)
                                onWinningUserPresentationComplete()
                            })
                        )
                    },
                }
            )
        )
    }, [winningUser])

    const isEqJackpot = jackpotType && jackpotType === JackpotType.JACKPOT_EGYPT_QUEST
    const displayedUsername = shouldHideJackpotWinnerNames ? '' : shownUsername

    const {
        panelOuter: panelOuterConfig,
        amountBox: amountBoxConfig,
        odometer: odometerConfig,
        winningUser: winningUserConfig,
        currency: currencyConfig,
    } = viewportConfig.jackpotStatistics

    const panelOuterStyle = {
        width: `${panelOuterConfig.width}px`,
        height: `${panelOuterConfig.height}px`,
        margin: panelOuterConfig.marginLeft
            ? `0 0 0 ${panelOuterConfig.marginLeft}px`
            : `0 ${panelOuterConfig.marginBetween}px`,
    }

    const winningUserStyle = {
        top: `${winningUserConfig.top}px`,
        color: winningUserConfig.fontColor,
        fontSize: `${winningUserConfig.fontSize}px`,
        lineHeight: `${winningUserConfig.lineHeight}px`,
        fontWeight: winningUserConfig.fontWeight,
    }

    const amountBoxStyle = {
        width: `${amountBoxConfig.width}px`,
        height: `${amountBoxConfig.height}px`,
        fontSize: `${amountBoxConfig.fontSize}px`,
        left: `calc(50% + ${amountBoxConfig.leftSymbolPadding}px)`,
        top: `${amountBoxConfig.top}px`,
    }

    const odometerWrapperStyle = {
        top: `${shownUsername && !isEqJackpot ? odometerConfig.winActiveTop : 0}px`,
    }

    const currencyStyle = {
        paddingBottom: `${currencyConfig.paddingBottom}px`,
        right: `${currencyConfig.right}px`,
        fontSize: `${currencyConfig.fontSize}px`,
        lineHeight: `${currencyConfig.lineHeight}px`,
        color: currencyConfig.fontColor,
        fontWeight: currencyConfig.fontWeight,
        width: `${currencyConfig.width}px`,
    }

    return (
        <div
            id={`jackpot-statistics-${type.toLowerCase()}`}
            className={`jackpot-statistics-${type.toLowerCase()} relative font-roboto`}
            style={panelOuterStyle}
            onClick={onClick}
            onMouseOver={onMouseOver}
            onMouseOut={onMouseOut}
        >
            <Slot slot="below">{children}</Slot>

            {/* Background image for the panel */}
            <img
                src={viewportConfig.images.panel}
                alt={`${type} Background`}
                className="absolute inset-0 w-full h-full object-fill"
            />

            {/* Jackpot amount */}
            <div
                ref={amountContainerRef}
                className="amount-box absolute translate-x-[-50%] text-white overflow-hidden"
                style={amountBoxStyle}
            >
                {shownUsername && !isEqJackpot && (
                    <div
                        className="absolute w-full text-center text-ellipsis overflow-hidden whitespace-nowrap"
                        style={winningUserStyle}
                    >
                        {displayedUsername}
                    </div>
                )}
                <div
                    className="odometer-wrapper absolute w-full h-full flex items-center justify-center"
                    style={odometerWrapperStyle}
                >
                    <JackpotOdometer
                        type={type}
                        config={config}
                        currency={currency}
                        showCurrencyCode
                        hideCents={hideCents}
                        animationDurationSeconds={duration}
                        countdownToZeroDurationSeconds={countdownToZeroDuration}
                        orientation={viewport}
                        realTimeAmount={realTimeAmount}
                        amount={amount}
                        shouldUpdateAmountInstantly={isAmountStatic}
                        useOdometer={shouldUseOdometer}
                        containerRef={amountContainerRef}
                        onRealTimeOdometerUpdate={onRealTimeOdometerUpdate}
                    />
                </div>
            </div>

            {/* Jackpot currency */}
            {currency && (
                <div
                    className="currency absolute top-1/2 translate-y-[-50%] flex flex-col items-center uppercase"
                    style={currencyStyle}
                >
                    {currency.split('').map((char: string, index: number) => (
                        <span key={index}>{char}</span>
                    ))}
                </div>
            )}

            {/* Only for Jackpot EQ */}
            {isPanelActive && <JackpotStatisticsEQShining type={type as JackpotEQLevels} />}

            <Slot slot="above">{children}</Slot>

            {/* Only for Jackpot EQ */}
            {isEqJackpot && (
                <>
                    <div
                        className="top-[2px] absolute w-2/3 h-[31px] overflow-hidden inset-x-0 mx-auto -z-[1] opacity-0 transform transition-all duration-500 ease-out starting:translate-y-0"
                        style={{
                            transform: `translateY(${displayedUsername ? panelOuterConfig.height - 4 : 0}px)`,
                            opacity: displayedUsername ? 1 : 0,
                        }}
                    >
                        <img
                            src={viewportConfig.images.usernamePanel}
                            alt={`${type} Winning user background`}
                            className="absolute inset-0 w-full h-full object-fill"
                        />
                        <span
                            ref={eqUsernameRef}
                            className="absolute w-full text-center text-ellipsis overflow-hidden whitespace-nowrap"
                            style={winningUserStyle}
                        >
                            {displayedUsername}
                        </span>
                    </div>
                </>
            )}
        </div>
    )
}

export default JackpotStatisticsItem
